import { $ } from '@zimpa/dom';
import { FocusTrap, focusTrap } from '../focus-trap';
import { ScrollBlock } from '../util/ScrollBlock';

const ROOT_SELECTOR = '.js-modal';
const OVERLAY_SELECTOR = '.js-modal__overlay';
const CLOSE_SELECTOR = '.js-modal__close';
const BODY_SELECTOR = '.js-modal__body';
const CONTENT_SELECTOR = '.js-modal__content';

const ENABLED_CLASS = 'enabled';
const OPEN_CLASS = 'open';
const BODY_OPEN_CLASS = 'c-modal__open';

const KEY_ESCAPE = 'Escape';

export class Modal {
    private static _instance: Modal | undefined;

    private _isOpen = false;
    private _interval: number | undefined;
    private readonly _body: HTMLElement;
    private readonly _close: HTMLElement;
    private readonly _content: HTMLElement;
    private readonly _focusTrap: FocusTrap | undefined;
    private readonly _overlay: HTMLElement;
    private readonly _root: HTMLElement;

    private constructor() {
        this._root = <HTMLElement>$(ROOT_SELECTOR);
        this._overlay = <HTMLElement>$(OVERLAY_SELECTOR, this._root);
        this._close = <HTMLElement>$(CLOSE_SELECTOR, this._root);
        this._content = <HTMLElement>$(CONTENT_SELECTOR, this._root);
        this._body = <HTMLElement>$(BODY_SELECTOR, this._root);

        if (this._content !== null) {
            this._focusTrap = focusTrap(this._content);
        }

        this.addEventHandlers();
    }

    private static getInstance(): Modal {
        if (this._instance === undefined) {
            this._instance = new Modal();
        }

        return this._instance;
    }

    public static open(body: string | HTMLElement): void {
        const modal = this.getInstance();

        modal.body = body;
        modal.show();
    }

    public static close(): void {
        if (this._instance === undefined) {
            return;
        }

        this._instance.hide();
    }

    private set body(body: string | HTMLElement) {
        if (typeof body === 'string') {
            this._body.innerHTML = body;

            return;
        }

        this._body.innerHTML = '';
        this._body.appendChild(body);
    }

    private addEventHandlers(): void {
        this._close.addEventListener('click', (): void => {
            this.hide();
        });

        this._overlay.addEventListener('click', (): void => {
            this.hide();
        });

        window.addEventListener('keydown', (event: KeyboardEvent): void => {
            if (!this._isOpen) {
                return;
            }

            if (event.key === KEY_ESCAPE) {
                this.hide();
            }
        });
    }

    private show(): void {
        ScrollBlock.enable();
        this._root.classList.add(ENABLED_CLASS);
        this._isOpen = true;

        window.clearTimeout(this._interval);

        this._interval = window.setTimeout((): void => {
            this._root.classList.add(OPEN_CLASS);
            document.body.classList.add(BODY_OPEN_CLASS);
        }, 50);

        if (this._focusTrap !== undefined) {
            this._focusTrap.enable();
        }
    }

    private hide(): void {
        if (this._focusTrap !== undefined) {
            this._focusTrap.disable();
        }

        ScrollBlock.disable();
        this._root.classList.remove(OPEN_CLASS);
        document.body.classList.remove(BODY_OPEN_CLASS);
        this._isOpen = false;

        window.clearTimeout(this._interval);

        this._interval = window.setTimeout((): void => {
            this._root.classList.remove(ENABLED_CLASS);
        }, 50);
    }
}
